
import logger from '../../../utils/logger'
import createCRMApiClient from '../../../api/CRMApi'
import {
  format, parseISO, eachDayOfInterval,
} from 'date-fns'
import {
  ScheduleType,
  Timeslot, TimeslotData,
} from '../../../api/BookingCalendar'
import {
  isAmPmMorningTimeslot,
  isAmPmAfternoonTimeslot,
  transformTimeslot,
  AM_PM_EMPTY_SLOTS,
  formattedSlot,
} from '../calendarUtils'
import { Day } from '../Calendar'

export interface CalendarContextType {
  fullPremise: string,
  startDate: Date,
  endDate: Date,
  selectedTimeslot?: Timeslot | TimeslotData | null
  serviceType?: string,
  broadbandProduct?: string,
  scheduleType?: string,
  offset?: number,
}

export const getDaysForRange = async ({
  fullPremise,
  startDate,
  endDate,
  selectedTimeslot,
  serviceType,
  broadbandProduct,
  scheduleType,
}: CalendarContextType): Promise<Day[]> => {
  const client = createCRMApiClient()
  const payload = { serviceType, broadbandProduct }

  let timeslots = await client.bookingCalendar.getTimeslots(
    fullPremise,
    startDate,
    endDate,
    payload,
    scheduleType as ScheduleType | undefined
  )

  if (!timeslots || timeslots.length === 0) {
    logger.error('error', 'timeslots not found')
    return []
  }

  timeslots = timeslots.map((x) => {
    x._id = {
      startDayOfMonth: x?.date ? parseInt(format(new Date(x?.date), 'd'), 10) : 1,
      startMonth: x?.date ? parseInt(format(new Date(x?.date), 'M'), 10) : 1,
      startYear: x?.date ? parseInt(format(new Date(x?.date), 'yyyy'), 10) : 2022,
      startHour: x?.startDateTime ? parseInt(format(parseISO(x?.startDateTime), 'k'), 10) : 11,
      startDayOfWeek: x?.date ? parseInt(format(new Date(x?.date), 'i'), 10) : 1,
      startDayOfYear: x?.date ? parseInt(format(new Date(x?.date), 'd'), 10) : 1,
      startWeek: x?.date ? parseInt(format(new Date(x?.date), 'w'), 10) : 1,
      startDateTime: x.startDateTime,
      endDateTime: x.endDateTime,
    }
    return x
  })

  const allDaysInRange = eachDayOfInterval({ start: startDate, end: endDate })

  const days: Day[] = allDaysInRange.map((currentDate) => {
    const amPmTimes: Day['timeslots'] = {
      am: (timeslots ?? []).filter((x) => isAmPmMorningTimeslot(x, currentDate))
        .map(transformTimeslot),
      pm: (timeslots ?? []).filter((x) => isAmPmAfternoonTimeslot(x, currentDate))
        .map(transformTimeslot),
    }

    AM_PM_EMPTY_SLOTS.am.forEach(([start, end]) => {
      const hasTimeslot = amPmTimes.am.some(t => t.start === start && t.end === end)
      if (!hasTimeslot) {
        amPmTimes.am.push({
          time: formattedSlot(start, end),
          start,
          end,
          available: false,
          timeslot: null,
        })
      }
    })

    AM_PM_EMPTY_SLOTS.pm.forEach(([start, end]) => {
      const hasTimeslot = amPmTimes.pm.some(t => t.start === start && t.end === end)
      if (!hasTimeslot) {
        amPmTimes.pm.push({
          time: formattedSlot(start, end),
          start,
          end,
          available: false,
          timeslot: null,
        })
      }
    })

    amPmTimes.am.sort((a, b) => a.start - b.start)
    amPmTimes.pm.sort((a, b) => a.start - b.start)

    const month = format(currentDate, 'LLL')
    const date = format(currentDate, 'd')
    const dayOfWeek = format(currentDate, 'EEE')

    const selectedMonth = selectedTimeslot && format(new Date(selectedTimeslot?.endDateTime), 'LLL')
    const selectedDate = selectedTimeslot && format(new Date(selectedTimeslot?.endDateTime), 'd')
    const selectedDayOfWeek = selectedTimeslot && format(new Date(selectedTimeslot?.endDateTime), 'EEE')

    const hasSelectedTimeslot = month === selectedMonth && date === selectedDate && dayOfWeek === selectedDayOfWeek

    return {
      month,
      date,
      dayOfWeek,
      available: amPmTimes.am.some(x => x.available) || amPmTimes.pm.some(x => x.available),
      hasSelectedTimeslot,
      timeslots: amPmTimes,
    }
  })

  return days
}
